import { useState } from 'react';
import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { MessageMetrics } from '@trinity/chat';
import { Tooltip } from './Tooltip.js';

const METRIC_KEYS = [
  'clarity',
  'motivating',
  'credibility',
  'differentiation',
  'objectiveResonance',
  'brandAlignment',
  'audienceFit',
  'relevance',
] as const satisfies readonly (keyof MessageMetrics)[];

function camelToTitle(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

interface MetricsPopoverProps {
  metrics: MessageMetrics;
  variant?: 'icon' | 'button';
}

export function MetricsPopover({ metrics, variant = 'icon' }: MetricsPopoverProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      {variant === 'button' ? (
        <ViewMetricsButton onClick={(e) => setAnchorEl(e.currentTarget)}>View Metrics</ViewMetricsButton>
      ) : (
        <MetricsIconButton onClick={(e) => setAnchorEl(e.currentTarget)} title="View Metrics">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="9" width="3" height="6" rx="0.5" />
            <rect x="5.5" y="5" width="3" height="10" rx="0.5" />
            <rect x="10" y="1" width="3" height="14" rx="0.5" />
          </svg>
        </MetricsIconButton>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        <PopoverContent>
          <PopoverHeader>
            <PopoverIcon>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="9" width="3" height="6" rx="0.5" />
                <rect x="5.5" y="5" width="3" height="10" rx="0.5" />
                <rect x="10" y="1" width="3" height="14" rx="0.5" />
              </svg>
            </PopoverIcon>
            <PopoverTitle>Metrics</PopoverTitle>
          </PopoverHeader>
          <Divider />
          <MetricsGrid>
            {[...METRIC_KEYS]
              .filter((key) => metrics[key] != null)
              .sort((a, b) => (metrics[b] ?? 0) - (metrics[a] ?? 0))
              .map((key) => {
              const value = metrics[key]!;
              const basisKey = `${key}Basis` as const;
              const basis = metrics[basisKey];
              const pct = Math.round(value * 100);
              return (
                <MetricItem key={key}>
                  <MetricLabelRow>
                    <MetricLabel>{camelToTitle(key)}</MetricLabel>
                    <MetricValue>{pct}%</MetricValue>
                  </MetricLabelRow>
                  {basis ? (
                    <Tooltip title={basis} enterDelay={500}>
                      <MetricBar>
                        <MetricFill style={{ width: `${pct}%` }} />
                      </MetricBar>
                    </Tooltip>
                  ) : (
                    <MetricBar>
                      <MetricFill style={{ width: `${pct}%` }} />
                    </MetricBar>
                  )}
                </MetricItem>
              );
            })}
          </MetricsGrid>
        </PopoverContent>
      </Popover>
    </>
  );
}

const MetricsIconButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  border: 'none',
  borderRadius: 6,
  background: '#f0f0f0',
  color: '#666',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#e0e0e0',
    color: '#565add',
  },
});

const ViewMetricsButton = styled('button')({
  padding: '4px 12px',
  border: '1px solid #d0d0d0',
  borderRadius: 6,
  background: 'white',
  color: '#555',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    background: '#f0f0f0',
    color: '#1271c4',
    borderColor: '#1271c4',
  },
});

const PopoverContent = styled('div')({
  width: 380,
});

const PopoverHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
});

const PopoverIcon = styled('span')({
  display: 'flex',
  alignItems: 'center',
  color: '#565add',
});

const PopoverTitle = styled('div')({
  fontSize: 14,
  fontWeight: 600,
  color: '#1a1a1a',
});

const Divider = styled('div')({
  height: 1,
  background: '#e8e8e8',
});

const MetricsGrid = styled('div')({
  display: 'grid',
  gridTemplateRows: 'repeat(4, auto)',
  gridAutoFlow: 'column',
  gridTemplateColumns: 'repeat(2, 1fr)',
  columnGap: 16,
  rowGap: 10,
  padding: '12px 16px 16px',
});

const MetricItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const MetricLabel = styled('span')({
  fontSize: 12,
  color: '#555',
  fontWeight: 500,
});

const MetricBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 18,
  background: '#f0f0f0',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'default',
});

const MetricFill = styled('div')({
  height: '100%',
  background: '#565add',
  borderRadius: 3,
  minWidth: 2,
  transition: 'width 0.3s ease',
});

const MetricLabelRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
});

const MetricValue = styled('span')({
  fontSize: 12,
  fontWeight: 600,
  color: '#333',
});
